import { callStated, callStatedList, emptyList, IList, NotificationService, updateEachListItem, withConfirmationService, withModalService } from '@aston/foundation';
import { signalStore, withHooks, withMethods } from '@ngrx/signals';
import { computed, effect, inject, untracked } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { patchState } from '@ngrx/signals';
import { withComputed } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';

import { ICompletedAccountingListItem, IImportConfiguration, IImportErrorColumnRowModel, IImportFileColumnMapping, IImportFileConfiguration, IImportFileReportErrorLineItem, IImportItem, IImportMappings } from '../../models';
import { ImportConfigurationStatus, ImportErrorHandling, ImportReportErrorColumnsListSortProp, ImportStatus, ImportStep, ImportType } from '../../enums';
import { ImportService } from '../../services';
import { IUserItem } from '../../../user-module/models';
import * as AppStoreSelectors from '../../../root-store/app-store/selectors';
import { UserService } from '../../../user-module/services';
import { UserClearanceLevel } from '../../../authentication-module/enums';
import { sortItemsByFileColumnName } from '../../functions';
import { getCurrentUserClearsLevel } from '../../../root-store/app-store/selectors';
import { withCurrentUser } from '../../../shared-module/stores/utils';

import { withImportDetailLoaders } from './import-detail.loaders';
import { withImportDetailListeners } from './import-detail.listeners';
import { withImportDetailStates } from './import-detail.states';
import { withImportDetailSteps } from './import-detail.steps';
import { withImportDetailVarious } from './import-detail.various';
import { withImportDetailGetters } from './import-detail.getters';
import { withImportDetailReports } from './import-detail.reports';

const IMPORT_ITEM = 'importItem';
const SELECTED_FILES = 'selectedFiles';
const IMPORT_CONFIG = 'importConfiguration';
const RECOVERY_ADVISORS = 'recoveryAdvisors';

const REPORT_ERROR_COLUMNS = 'reportErrorColumns';
const REPORT_ERROR_LINES = 'reportErrorLines';
const COMPLETED_ACCOUNTING = 'completedAccounting';

export interface IImportDetailStoreInitialState {
	importItem: IImportItem;
	selectedFiles: File[];
	importConfiguration: IImportConfiguration;
	recoveryAdvisors: IUserItem[];

	reportErrorColumns:  IList<IImportErrorColumnRowModel>;
	reportErrorLines: IList<IImportFileReportErrorLineItem>;
	completedAccounting: IList<ICompletedAccountingListItem>;

	synthesisModalRef: NgbModalRef;
}

const initialState: IImportDetailStoreInitialState = {
	importItem: null as IImportItem,
	selectedFiles: [] as File[],
	importConfiguration: null as IImportConfiguration,
	recoveryAdvisors: [] as IUserItem[],

	reportErrorColumns: emptyList<IImportErrorColumnRowModel>(),
	reportErrorLines: emptyList<IImportFileReportErrorLineItem>(),
	completedAccounting: emptyList<ICompletedAccountingListItem>(),

	synthesisModalRef: null as NgbModalRef,
}

export const ImportDetailStore = signalStore(
	{ providedIn: 'root', protectedState: false },
	
	withImportDetailStates(initialState),

	withConfirmationService(),
	withModalService({ collection: 'importDetail' }),
	withCurrentUser(),

	withComputed(store => {
		const appState = toSignal(inject(Store).select(AppStoreSelectors.selectState));
		
		const importConfigStatus = computed(() => !store.importItem()?.importFileConfiguration ? ImportConfigurationStatus.None : store.importItem().importFileConfiguration.importConfigurationStatus);

		const importIsDisabled = computed(() => {
			if (!store.importItem()) return true;

			const allowed = getCurrentUserClearsLevel(appState(), UserClearanceLevel.ManualImport);
			const pendingConfig = importConfigStatus() === ImportConfigurationStatus.Preparing;

			return !allowed || pendingConfig;
		});

		const reportValidationStepIsDisabled = computed(() => {
			const importItem = store.importItem();
			
			if (!importItem) return true;

			if (importItem.importFileConfiguration?.rejectWholeFile
				&& importItem.importFileConfiguration?.rejectWholeFile === ImportErrorHandling.Complete
				&& importItem.synthesis.notImportedLineCount !== 0) {
				return true;
			}

			return importItem.importFileConfiguration?.rejectWholeFile
				&& importItem.importFileConfiguration?.rejectWholeFile === ImportErrorHandling.BestEffort
				&& (importItem.synthesis.totalReadLineCount - importItem.synthesis.notImportedLineCount) <= 0;
		});
		
		return {
			currentType: computed(() => store.importItem()?.type),
			importConfigStatus,
			importIsDisabled,
			reportValidationStepIsDisabled,
			configWorkerMessage: computed(() => !store.importItem()?.importFileConfiguration ? null : store.importItem().importFileConfiguration.extraMessage),
			blocked: computed(() => importConfigStatus() === ImportConfigurationStatus.Preparing),
			importIsFinished: computed(() => store.importItem().status === ImportStatus.Done),
		}
	}),

	withImportDetailLoaders(),

	withMethods(store => {
		const service = inject(ImportService);
		const usersService = inject(UserService);
		const toasts = inject(NotificationService);

		const updateSelectedFiles = () => {
			patchState(store, {
				selectedFiles: store.importItem().file
					? [{ name: store.importItem().file.name }] as File[]
					: [],
			})
		}
		
		const updateImportFileConfigStatus = () => {
			const importFileConfiguration = {
				...store.importItem()?.importFileConfiguration,
				importConfigurationStatus: ImportConfigurationStatus.Preparing
			};

			patchState(store, { importItem: { ...store.importItem(), importFileConfiguration } });
		}		

		const emptyImportOfType = (type: ImportType): IImportItem => {
			return {
				type,
				status: ImportStatus.Draft,
				currentStep: ImportStep.UploadFile,
				// reset everything but these
				id: null,
				functionalId: null,
				file: null,
				fileExtract: null,
				importFileConfiguration: null,
				columnsMapping: null,
				documentTypesMapping: null,
				paymentTypesMapping: null,
				operationTypesMapping: null,
				currenciesMapping: null,
				countriesMapping: null,
				booleanMapping: null,
				languagesMapping: null,
				recoveryAdvisorsMapping: null,
				civilityMapping: null,
				sectorMapping: null,
				report: null,
				completedDocument: null,
				synthesis: null,
			};
		}

		const init = () => loadImport().then(_ => {
			const justImported = store.importItem()?.currentStep === ImportStep.Imported;

			patchState(store, {
				...initialState,
				importConfiguration: store.importConfiguration(),
				importItem: justImported ? emptyImportOfType(store.importItem().type) : store.importItem(),
			})

			updateSelectedFiles()
		})

		const loadImport = () => {
			const call = () => store.currentType() ? service.getDraftImport(store.currentType()) : service.getLastDraftImport();
			return callStated(call(), store, IMPORT_ITEM).then(_ => updateSelectedFiles());
		}

		const loadCompletedAccounting = () => callStatedList(service.getCompletedAccountingOfImport(store.importItem().id, store.completedAccountingListState()), store, COMPLETED_ACCOUNTING)

		const loadRecoveryAdvisors = () => callStated(usersService.getUsersByClearanceLevel(UserClearanceLevel.Dunning), store, RECOVERY_ADVISORS);
		
		const loadImportSynthesis = () => callStated(service.getImport(store.importItem().id), store, IMPORT_ITEM);
		
		const loadReportErrorColumns = () => {
			const importItem = store.importItem();
			const { filters, ...paging } = store.reportErrorColumnsListState();

			// do we sort by column name ?
			const columnSort = paging.sorts.find(s => s.prop === ImportReportErrorColumnsListSortProp.COLUMN)
			const sortByColumnName = columnSort ? columnSort.prop : null;
			const isDesc = columnSort ? columnSort.isDesc : false;

			// get the columns defs, to match extradata with actual headers
			const columnDefs = store.importConfiguration()?.importColumns.find(c => c.importType === importItem?.type)?.importColumns || [];

			if (!importItem?.id) return;

			return callStatedList(service.getReportErrorColumns(importItem.id, { ...paging, filters }, columnDefs), store, REPORT_ERROR_COLUMNS)
				.then(list => ({
					...list,
					items: !sortByColumnName ? list.items : sortItemsByFileColumnName(list.items, importItem.columnsMapping, isDesc),
				}))
				.then(_ => patchState(store, updateEachListItem(
					(row: IImportFileReportErrorLineItem) => ({
						...row,
						reportErrorLinesListPaging: { ...paging }
					}),
					REPORT_ERROR_LINES))
				)
		}

		const loadImportConfig = () => callStated(service.getImportConfiguration(), store, IMPORT_CONFIG);

		const updateImportFileConfig = (config: IImportFileConfiguration) =>
			callStated(service.updateDraftImportConfiguration(store.currentType(), config), store, IMPORT_ITEM)
				.then(_ => loadImport())
			
		const updateHeadersMapping = (mapping: IImportFileColumnMapping[]) =>
			callStated(service.updateColumnsMapping(store.currentType(), mapping), store, IMPORT_ITEM, { stateOnly: true })
				.then(_ => {
					loadImport();
					setCurrentStep(ImportStep.MappingValue);
				})
				.catch(_ => setCurrentStep(ImportStep.ColumnPosition))

		const updateValuesMapping = (mappingData: IImportMappings) => {
			const { adType, paymentType, operationType, invoicingCurrency, country, civility, recoveryAdvisor, language, boolean, sector } = mappingData;

			const call = {
				[ImportType.Accounting]: () => service.updateValuesMappingAccounting(store.currentType(), adType, paymentType, invoicingCurrency),
				[ImportType.Cashing]: () => service.updateValuesMappingCashing(store.currentType(), adType, paymentType, operationType),
				[ImportType.Contact]: () => service.updateValuesMappingContact(store.currentType(), civility, language, boolean),
				[ImportType.Debtor]: () => service.updateValuesMappingDebtor(store.currentType(), country, civility, recoveryAdvisor, language, sector),
			}[store.currentType()] || (() => Promise.resolve())

			return callStated(call(), store, IMPORT_ITEM, { stateOnly: true })
				.then(_ => {
					toasts.success({ message: 'Import.Creation.Notifications.DataMappingSuccess', autoClose: true })
					updateImportFileConfigStatus();
				})
		}

		const setCurrentStep = (currentStep: ImportStep) => patchState(store, { importItem: { ...store.importItem(), currentStep } });

		const canUpdate = () => {
			if (store.currentType() === ImportType.Accounting || store.currentType() === ImportType.Cashing) {
				return store.currentUserCan(UserClearanceLevel.ManualImport);
			}
			else return false;
		};

		return {
			init,

			loadImport,
			loadCompletedAccounting,
			loadRecoveryAdvisors,
			loadImportSynthesis,
			loadReportErrorColumns,
			loadImportConfig,
			
			updateImportFileConfig,
			updateHeadersMapping,
			updateValuesMapping,
			
			setCurrentStep,
			canUpdate,
		}
	}),
	
	withImportDetailListeners(),
	withImportDetailSteps(),
	
	withImportDetailVarious(IMPORT_ITEM, SELECTED_FILES),
	withImportDetailGetters(),

	withImportDetailReports(REPORT_ERROR_COLUMNS, REPORT_ERROR_LINES),
	
	withHooks({
		onInit: store => {
			store.listenReloadImportConfig().subscribe();
			store.listenAccountingDraftImportCancelledEvent().subscribe();
			store.listenReloadImportEvent().subscribe();
			store.listenReloadImportWithErrorEvent().subscribe();
			store.listenCompleteImportEvent().subscribe();
			store.listenGlobalCompleteImportEvent().subscribe();
			store.listenGlobalWorkflowComplete().subscribe();
			store.listenGlobalWorkflowFailure().subscribe();

			effect(() => {
				const first = untracked(store.completedAccountingCallState) === 'init'
				// when
				store.completedAccountingFilters()
				store.completedAccountingPaging()
				// then
				if (!first) untracked(store.loadCompletedAccounting)
			})
		},
	})

);

export type ImportDetailStore = InstanceType<typeof ImportDetailStore>;
